import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';

import AssetService from '../../Services/AssetService';
import TemplateService from '../../Services/TemplateService';
import OrgsMapContext from '../../Context/OrgsMapContext';
import {
  DF_PROVISIONING_MODEL_ID,
  PROVISIONING_MODEL_ID,
  XIRGO_PROVISIONING_MODEL_ID,
  XIRGO_V2_PROVISIONING_MODEL_ID,
} from '../../Constants/StaticUUIDs';
import {
  XIRGO_V1_VENDOR_ID,
  XIRGO_V2_VENDOR_ID,
} from '../../Constants/Vendors';

import MoveAssetModalDisplay, {
  transformTemplateData,
} from './MoveAssetModalDisplay';

export default memo(MoveAssetModal);

function MoveAssetModal(props) {
  const [state, updateState] = useState(() => {
    const modelType = [
      DF_PROVISIONING_MODEL_ID,
      PROVISIONING_MODEL_ID,
      XIRGO_PROVISIONING_MODEL_ID,
      XIRGO_V2_PROVISIONING_MODEL_ID,
    ].includes(props.selectedAsset?.gtemplateID)
      ? 'keepProvision'
      : props.selectedAsset?.vendor === XIRGO_V2_VENDOR_ID
        ? 'selectModel'
        : 'cloneModel';
    return {
      error: '',
      modelType,
      moveRequestPending: false,
      orgNameSearch: '',
      orgTemplateOptions: [],
      orgTemplatesLoading: true,
      selectedOrgID: '',
      viewing: 'select',
    };
  });

  const orgsMap = useContext(OrgsMapContext);

  const source = CancelToken.source();
  const templateService = useMemo(() => new TemplateService(), []);

  const handleOrgNameSearch = ({ target: { value } }) => {
    updateState({ ...state, orgNameSearch: value });
  };

  const handleBackClick = () => {
    updateState({ ...state, viewing: 'select', selectedOrgID: '' });
  };

  const handleOrgSelect = (id) => {
    const update = state.selectedOrgID.includes(id) ? '' : id;
    updateState({ ...state, selectedOrgID: update });
  };

  const handleMoveButtonClick = () => {
    updateState({ ...state, viewing: 'confirm' });
  };

  const handleModelTypeSelect = ({ target: { name, value } }) => {
    updateState({ ...state, [name]: value, selectedModel: null });
  };

  const handleModelSelect = (selectedModel) => {
    updateState({ ...state, selectedModel });
  };

  const handleMoveOrg = async () => {
    updateState({ ...state, moveRequestPending: true });
    try {
      const {
        currentOrg: { gatewayCount = 0 },
        handleAssetWasMoved,
        handleSubtractAsset,
        selectedAsset: { _id: assetID, gtemplateID, nickname, vendor },
        toggleModal,
        updateCurrentOrg,
      } = props;
      const { modelType, selectedModel, selectedOrgID: newOrg } = state;

      const { path = '' } = orgsMap[newOrg] ?? {};
      const validSharedGtemplate = path.includes(state.sharedGtemplate?.orgID);

      if (
        ![
          DF_PROVISIONING_MODEL_ID,
          PROVISIONING_MODEL_ID,
          XIRGO_PROVISIONING_MODEL_ID,
          XIRGO_V2_PROVISIONING_MODEL_ID,
        ].includes(gtemplateID) &&
        !selectedModel?.value &&
        modelType !== 'keepShared'
      ) {
        throw new Error('A model must be selected for the Asset');
      }

      if (
        validSharedGtemplate &&
        modelType !== 'keepShared' &&
        !selectedModel?.value
      ) {
        throw new Error(
          'New model must be selected if not keeping Shared Model'
        );
      }

      if (modelType === 'cloneModal' && vendor === XIRGO_V2_VENDOR_ID) {
        throw new Error('EZ+ model clone not supported');
      }

      const selectedModelID =
        validSharedGtemplate && modelType === 'keepShared'
          ? { selectedModelID: gtemplateID }
          : selectedModel
            ? { selectedModelID: selectedModel.value }
            : {};
      const body = {
        newOrg,
        ...selectedModelID,
        cloneSharedTemplate: modelType === 'cloneModel',
      };
      const updatedAsset = await new AssetService().moveAssetToNewOrg(
        assetID,
        body
      );

      const successMessage = `${nickname || 'Asset'} has been moved to ${
        orgsMap[newOrg].name
      }`;
      if (handleSubtractAsset) {
        handleSubtractAsset(assetID, successMessage);
        toggleModal();
      } else {
        updateCurrentOrg({ gatewayCount: gatewayCount - 1 });
        handleAssetWasMoved(updatedAsset);
      }
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, moveRequestPending: false });
    }
  };

  const fetchInitData = async () => {
    try {
      const { orgID, vendor: assetVendor } = props.selectedAsset || {};
      const limit = 100;
      const order = 'asc';
      const page = 1;
      const sort = 'name';
      const vendor =
        assetVendor === XIRGO_V1_VENDOR_ID
          ? XIRGO_V1_VENDOR_ID
          : assetVendor === XIRGO_V2_VENDOR_ID
            ? XIRGO_V2_VENDOR_ID
            : assetVendor === 'danfoss'
              ? '1'
              : 'elevat';

      const [{ count, data }, { sharedGtemplate }] = await Promise.all([
        templateService.fetchPaginatedTemplates(
          page,
          orgID,
          limit,
          sort,
          order,
          undefined,
          vendor,
          source.token
        ),
        templateService.fetchTemplate(
          props.selectedAsset?.gtemplateID,
          source.token
        ),
      ]);
      const availableTemplates = transformTemplateData(data);
      if (count <= data.length) {
        updateState((prevState) => ({
          ...prevState,
          modelType: sharedGtemplate ? 'keepShared' : state.modelType,
          orgTemplatesLoading: false,
          orgTemplateOptions: availableTemplates,
          sharedGtemplate,
        }));
      } else {
        const additionalData =
          await templateService.handleAdditionalTemplateRequests({
            cancelToken: source.token,
            count,
            limit,
            order,
            orgID,
            sort,
            vendor,
          });

        const addtionalTemplateOptions = transformTemplateData(additionalData);
        updateState((prevState) => ({
          ...prevState,
          modelType: sharedGtemplate ? 'keepShared' : state.modelType,
          orgTemplatesLoading: false,
          orgTemplateOptions: availableTemplates.concat(
            ...addtionalTemplateOptions
          ),
          sharedGtemplate,
        }));
      }
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        orgTemplatesLoading: false,
      }));
    }
  };

  useEffect(() => {
    fetchInitData();
    return source.cancel;
  }, []);

  return (
    <MoveAssetModalDisplay
      {...props}
      {...state}
      handleBackClick={handleBackClick}
      handleModelSelect={handleModelSelect}
      handleModelTypeSelect={handleModelTypeSelect}
      handleMoveButtonClick={handleMoveButtonClick}
      handleMoveOrg={handleMoveOrg}
      handleOrgSelect={handleOrgSelect}
      handleOrgNameSearch={handleOrgNameSearch}
      orgsMap={orgsMap}
    />
  );
}

MoveAssetModal.propTypes = {
  currentOrg: PropTypes.object,
  handleAssetWasMoved: PropTypes.func,
  handleSubtractAsset: PropTypes.func,
  isOpen: PropTypes.bool,
  selectedAsset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplateID: PropTypes.string,
    nickname: PropTypes.string,
    orgID: PropTypes.string,
    vendor: PropTypes.string,
  }),
  toggleModal: PropTypes.func,
  updateCurrentOrg: PropTypes.func,
};
